/**
 * Claim Modal Module
 *
 * Handles the claim flow:
 * 1. Show claim confirmation with display name input
 * 2. Call API to create Stripe Checkout session
 * 3. Redirect to Stripe
 */
import { supabase } from "./api";
import { store, actions } from "./store";
import { isCoordinateClaimed, uploadThumbnail } from "./db";
import { renderParcelThumbnailCached } from "./thumbnailRenderer";
import type { Parcel } from "./store";
import { trackEvent } from "./analytics";

export function initClaimModal(): void {
  const claimModal = document.getElementById("claimModal") as HTMLDialogElement | null;
  const claimCancel = document.getElementById("claimCancel");
  const claimConfirm = document.getElementById("claimConfirm");
  const claimCoordRe = document.getElementById("claimCoordRe");
  const claimCoordIm = document.getElementById("claimCoordIm");
  const claimCoordZoom = document.getElementById("claimCoordZoom");
  const claimError = document.getElementById("claimError");

  if (!claimModal) return;

  claimCancel?.addEventListener("click", () => {
    claimModal.close();
    actions.setClaimingParcelId(null);
  });

  claimModal.addEventListener("click", (e) => {
    if (e.target === claimModal) {
      claimModal.close();
      actions.setClaimingParcelId(null);
    }
  });

  claimConfirm?.addEventListener("click", async () => {
    const parcelId = store.getState().claimingParcelId;
    if (!parcelId) return;

    const parcel = store.getState().ownedParcels.find((p) => p.id === parcelId);
    if (!parcel) return;

    const displayName = parcel.name || "Untitled";

    // Hide error
    if (claimError) claimError.textContent = "";

    // Disable button
    if (claimConfirm) {
      claimConfirm.textContent = "Processing...";
      (claimConfirm as HTMLButtonElement).disabled = true;
    }

    try {
      // Check if coordinate already claimed
      const alreadyClaimed = await isCoordinateClaimed(parcel.re, parcel.im, parcel.zoom);
      if (alreadyClaimed) {
        if (claimError) claimError.textContent = "This coordinate is already claimed by someone else.";
        return;
      }

      // Generate and upload thumbnail before redirect
      try {
        console.log("[Claim] Rendering thumbnail...");
        const dataUrl = await renderParcelThumbnailCached(parcel, 512);
        console.log("[Claim] Thumbnail rendered, size:", dataUrl.length);
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        console.log("[Claim] Blob created, size:", blob.size);
        const url = await uploadThumbnail(parcel.id, blob);
        console.log("[Claim] Thumbnail uploaded, url:", url);
      } catch (thumbError) {
        console.error("[Claim] Thumbnail upload failed, continuing with claim:", thumbError);
      }

      // Get auth token
      if (!supabase) {
        if (claimError) claimError.textContent = "Not connected to server.";
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (claimError) claimError.textContent = "Please log in to claim.";
        return;
      }

      // Call API to create checkout session
      const response = await fetch("/api/create-claim-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          parcelId,
          displayName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (claimError) claimError.textContent = data.error || "Failed to create checkout.";
        return;
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Claim error:", error);
      if (claimError) claimError.textContent = "An error occurred. Please try again.";
    } finally {
      if (claimConfirm) {
        claimConfirm.textContent = "Claim for €3";
        (claimConfirm as HTMLButtonElement).disabled = false;
      }
    }
  });
}

/**
 * Open the claim modal for a specific parcel
 */
export function openClaimModal(parcel: Parcel): void {
  const claimModal = document.getElementById("claimModal") as HTMLDialogElement | null;
  const claimCoordRe = document.getElementById("claimCoordRe");
  const claimCoordIm = document.getElementById("claimCoordIm");
  const claimCoordZoom = document.getElementById("claimCoordZoom");
  const claimError = document.getElementById("claimError");

  if (!claimModal) return;

  // Set parcel info
  if (claimCoordRe) claimCoordRe.textContent = parcel.re.toFixed(6);
  if (claimCoordIm) claimCoordIm.textContent = parcel.im.toFixed(6);
  if (claimCoordZoom) claimCoordZoom.textContent = String(parcel.zoom);

  // Clear error
  if (claimError) claimError.textContent = "";

  actions.setClaimingParcelId(parcel.id);
  claimModal.showModal();
  trackEvent("claim_start", { parcelId: parcel.id });
}
